import { clsx } from "clsx";
import {
  area,
  axisLeft,
  curveMonotoneX,
  curveStepAfter,
  extent,
  line,
  type ScaleLinear,
  scaleLinear,
  select,
} from "d3";
import { type FC, useEffect, useRef } from "react";
import { useHover } from "react-aria";

import { buildRasterSlices, type IRasterSegment } from "@/app-utils/rasterization-service.ts";
import { conditionallyPluralizeTrackLabel } from "@/app-utils/string.ts";
import { type Range, type Track } from "@/graphql.ts";
import { CANVAS_TRACK_MARGIN } from "@/settings/genome-viewer-settings.ts";
import { PROFILE_TRACK_HEIGHT } from "@/settings/settings.js";
import { ShowMessageOverTrack } from "@/ui-modules/genome-viewer/GenomeViewerTracksList.tsx";

import styleTrack from "./styles/Track.module.scss";
import { TrackHeader } from "./TrackHeader.js";

export interface IProfileTrackProps {
  range: Range;
  track: Track;
  rasterData: IRasterSegment[];
  trackWidth: number;
  overflow: boolean;
  interpolate?: boolean;
}

/**
 * A visualization component that renders a profile genome browser track.
 *
 * The component is expected to be used within an existing SVG context and does NOT create its own SVG element. The
 * main input data is a pre-computed raster data of numeric profile features. During the rastering phase each segment
 * is assigned view coordinates and a width.
 *
 * created on 8/17/2017
 */
const ProfileTrack: FC<IProfileTrackProps> = (props) => {
  const containerRef = useRef<SVGGElement>(undefined);
  const axisRef = useRef<SVGGElement>(undefined);

  const { hoverProps, isHovered } = useHover({});

  const lineElements = useRef([]);
  const areaElements = useRef([]);

  useEffect(() => {
    // Cleanup old elements
    if (areaElements.current.length > 0) {
      areaElements.current.forEach((e) => {
        e.remove();
      });
      areaElements.current = [];
    }
    if (lineElements.current.length > 0) {
      lineElements.current.forEach((e) => {
        e.remove();
      });
      lineElements.current = [];
    }

    const trackHeight: number = PROFILE_TRACK_HEIGHT;

    const rasterData = props.rasterData || [];
    const canvasWidth = props.trackWidth;
    const canvasHeight: number = trackHeight - 3;

    const valueRange = extent(rasterData.map((s: IRasterSegment) => s.value).concat([0]));
    const data = rasterData.map((s: IRasterSegment) => [props.interpolate ? s.mid : s.x, s.value]);
    if (data.length) {
      data.push([canvasWidth, data[data.length - 1][1]]); // needed to make the histogram is drawn properly at the end
    }

    // build the data slices, splitting the data at y=0 values and omitting 0-only areas
    const slicedData: number[][][] = buildRasterSlices(data);

    const yScale: ScaleLinear<number, number> = scaleLinear().range([canvasHeight, 0]).domain(valueRange).nice();
    const interpolation = props.interpolate ? curveMonotoneX : curveStepAfter;
    const valueLine = line()
      .curve(interpolation)
      .x((d) => d[0])
      .y((d) => Math.round(yScale(d[1])));
    const d3Area = area()
      .curve(interpolation)
      .x((d) => d[0])
      .y0(canvasHeight + 1)
      .y1((d) => Math.round(yScale(d[1])));

    const yAxis = axisLeft(yScale).ticks(5);

    // Render area and top line
    const container = select(containerRef.current);

    slicedData.forEach((s) => {
      areaElements.current.push(
        container
          .append("path")
          .datum(s)
          .attr("class", "area")
          .attr("d", d3Area as any),
      );
      lineElements.current.push(
        container
          .append("path")
          .datum(s)
          .attr("class", "line")
          .attr("d", valueLine as any),
      );
    });

    // Render y-axis
    select(axisRef.current).call(yAxis);
    const tickElements = select(axisRef.current).selectAll(".tick");

    // Remove first tick elements
    tickElements.each(function (_d, i) {
      if (i === 0) {
        (this as any).remove();
      }
    });
    select(axisRef.current).selectAll(".domain").remove();
  }, [props.interpolate, props.rasterData, props.trackWidth]);

  return (
    <div className={"feature-track"}>
      <div className={clsx(styleTrack.track, { [styleTrack.track_hovered]: isHovered })} {...hoverProps}>
        <TrackHeader title={conditionallyPluralizeTrackLabel(props.track)} isTrackHovered={isHovered}>
          <svg
            width="100%"
            height={PROFILE_TRACK_HEIGHT}
            style={{
              marginTop: CANVAS_TRACK_MARGIN, // fake the track top margin
              marginBottom: -CANVAS_TRACK_MARGIN, // compensate the track container padding = 0
              transform: "translate(100%, 1px)", // tick are rendered outside the track SVG + compensate track header right margin
              overflow: "visible",
            }}
          >
            <g className="feature-track-canvas" transform={`translate(0, 0.5)`}>
              <g className="axis" ref={axisRef} />
            </g>
          </svg>
        </TrackHeader>
        <div className={styleTrack.container} style={{ overflow: "visible", paddingBottom: 0 }}>
          <svg
            width="100%"
            height={PROFILE_TRACK_HEIGHT}
            key={`canvas-${props.track.name}`}
            style={{ flex: 1, overflow: "visible" }}
          >
            {props.overflow ? (
              <foreignObject x="0" y="0" width={"100%"} height={PROFILE_TRACK_HEIGHT}>
                <ShowMessageOverTrack message={"Zoom in to see the profile"} />
              </foreignObject>
            ) : (
              <g className="feature-track-canvas" transform={`translate(0, 1.5)`}>
                <g className="profile" transform={`translate(0.5, 0)`} ref={containerRef} />
              </g>
            )}
          </svg>
        </div>
      </div>
    </div>
  );
};

export { ProfileTrack };
